import type { DialogApiInjection } from "naive-ui/es/dialog/src/DialogProvider"
import type { Component, VNodeChild } from "vue"
import type { ComposerTranslation } from "vue-i18n"
import type { I18nString } from "@/lang/config"
import type { APIError } from "@/types/common"
import { isMobile as detectMobile } from "detect-touch-device"
import _split from "lodash/split"
import _toNumber from "lodash/toSafeInteger"
import { NAlert } from "naive-ui"
import { h } from "vue"
import Icon from "@/components/common/Icon.vue"
import JMButton from "@/components/common/JMButton.vue"

export type OS = "Unknown" | "Windows" | "MacOS" | "UNIX" | "Linux" | "iOS" | "Android"

const IOS_USER_AGENT_REGEX = /iPad|iPhone|iPod/
const ANDROID_USER_AGENT_REGEX = /Android/
const WINDOWS_USER_AGENT_REGEX = /Win(?:dows|16|32|64|CE|NT)|WOW64/
const WINDOWS_PLATFORM_REGEX = /Win/
const MACOS_USER_AGENT_REGEX = /Macintosh|MacIntel|MacPPC|Mac68K/
const MACOS_PLATFORM_REGEX = /Mac/
const LINUX_USER_AGENT_REGEX = /Linux/
const UNIX_USER_AGENT_REGEX = /X11|SunOS|BSD|HP-UX|AIX/
const TRAILING_DOT_REGEX = /\.$/

// Transform File Instance in base64 string
export function file2Base64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.readAsDataURL(blob)
		reader.onload = () => resolve(reader.result as string)
		reader.onerror = error => reject(error)
	})
}

export function isEnvDev() {
	return import.meta.env.DEV
}
export function isEnvTest() {
	return import.meta.env.MODE === "test"
}
export function isEnvProd() {
	return import.meta.env.PROD
}

export function isMobile() {
	return detectMobile
}

export function renderIcon(icon: Component | string) {
	if (typeof icon === "string") {
		return () => h(Icon, { name: icon })
	} else {
		return () => h(Icon, null, { default: () => h(icon) })
	}
}

export function getOS(): OS {
	const ua = navigator.userAgent
	const platform = navigator.platform || ""

	// iOS detection (iPhone, iPad, iPod)
	if (IOS_USER_AGENT_REGEX.test(ua) || (platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
		return "iOS"
	}

	// Android detection
	if (ANDROID_USER_AGENT_REGEX.test(ua)) {
		return "Android"
	}

	// Windows detection (include Windows Phone)
	if (WINDOWS_USER_AGENT_REGEX.test(ua) || WINDOWS_PLATFORM_REGEX.test(platform)) {
		return "Windows"
	}

	// MacOS detection (exclude iOS which was already checked)
	if (MACOS_USER_AGENT_REGEX.test(ua) || MACOS_PLATFORM_REGEX.test(platform)) {
		return "MacOS"
	}

	// Linux detection (exclude Android which was already checked)
	if (LINUX_USER_AGENT_REGEX.test(ua) && !ANDROID_USER_AGENT_REGEX.test(ua)) {
		return "Linux"
	}

	// UNIX detection
	if (UNIX_USER_AGENT_REGEX.test(ua)) {
		return "UNIX"
	}

	return "Unknown"
}

export function delay(t: number) {
	return new Promise(res => setTimeout(res, t))
}

export function truncateMiddle({
	value,
	length = 8,
	separator = "...",
	frontChars = 4,
	backChars = 4
}: {
	value: string
	length?: number
	separator?: string
	frontChars?: number
	backChars?: number
}): string {
	if (value.length <= length) return value

	return value.slice(0, frontChars) + separator + value.slice(-backChars)
}

function buildApiErrorMessage(responseData: unknown, fallbackMessage?: string): string {
	const errorData = responseData as {
		error?: string
		message?: string
		failed?: Record<string, string | string[]>
	}

	const apiError = typeof errorData?.error === "string" ? errorData.error : undefined
	const apiMessage = typeof errorData?.message === "string" ? errorData.message : undefined
	const apiDescription = Object.values(errorData?.failed ?? {})
		.flat(Number.POSITIVE_INFINITY)
		.filter((item): item is string => typeof item === "string")
		.map(item => String(item).trim().replace(TRAILING_DOT_REGEX, ""))
		.filter(Boolean)
		.join(". ")

	const fallback = typeof fallbackMessage === "string" ? fallbackMessage : ""
	const error = (apiDescription || apiMessage || apiError || fallback).trim()

	if (!error) return "Unknown error."

	return `${error.replace(TRAILING_DOT_REGEX, "")}.`
}

/** Accepts both an APIError and an already unwrapped `response.data` payload (see `unwrapApiError` in the stores). */
export function getApiErrorMessage(err: APIError | unknown): string {
	const error = err as APIError
	const responseData = error?.response?.data ?? error
	return buildApiErrorMessage(responseData, error?.message)
}

export function getApiErrorMessageBlob(err: APIError | unknown): Promise<string> {
	const error = err as APIError
	const responseData = error?.response?.data ?? error

	if (responseData instanceof Blob) {
		return responseData
			.text()
			.then(text => {
				try {
					return JSON.parse(text)
				} catch {
					return undefined
				}
			})
			.then(parsedData => buildApiErrorMessage(parsedData, error?.message))
	}

	return Promise.resolve(getApiErrorMessage(err))
}

export function getParamId(param: string | number): number {
	const paramText = param.toString()
	if (paramText.includes("-")) {
		const chunks = paramText.split("-")

		for (const chunk of chunks) {
			if (chunk === `${_toNumber(chunk)}`) {
				return _toNumber(chunk)
			}
		}
	}
	return _toNumber(paramText)
}

export function getParamLabel(param: string | number): string {
	const paramText = param.toString()
	if (paramText.includes("-")) {
		const chunks = paramText.split("-")

		for (const chunk of chunks) {
			if (chunk !== `${_toNumber(chunk)}`) {
				return decodeURI(chunk)
			}
		}
	}

	return decodeURI(paramText)
}

export function confirmDialog(
	dialog: DialogApiInjection,
	options: {
		title: string
		content?: string
		positiveText: string
		negativeText: string
		alertText?: string
		alertType?: "error" | "info" | "success" | "warning"
		success: () => void
		cancel?: () => void
	}
) {
	const dialogRef = dialog.warning({
		class: `p-0! w-[90vw]! max-w-132! ${options.content || options.alertText ? "divide-y" : ""}`,
		title: options.title,
		titleClass: "text-fs-base! font-medium! px-5! py-4!",
		content: () =>
			(options.content || options.alertText) &&
			h(
				"div",
				{
					class: "flex flex-col gap-2"
				},
				[
					options.content &&
						h(
							"div",
							{
								class: "text-fs-base font-semibold"
							},
							options.content
						),
					options.alertText &&
						h(
							NAlert,
							{
								type: options.alertType
							},
							{
								icon: () => h(Icon, { name: "carbon:information" }),
								default: () => h("div", { class: "mt-px" }, options.alertText)
							}
						)
				]
			),
		contentClass: options.content || options.alertText ? "p-6! my-0!" : "p-0! my-0!",
		actionClass: "p-6!",
		closeFocusable: false,
		showIcon: false,
		bordered: false,
		action: () =>
			h(
				"div",
				{
					class: "flex flex-wrap items-center justify-end gap-4"
				},
				[
					h(
						JMButton,
						{
							type: "secondary",
							size: "large",
							focusable: false,
							onClick: (e: MouseEvent) => {
								e.preventDefault()
								dialogRef?.onNegativeClick?.(e)
								dialogRef?.destroy()
							}
						},
						{ default: () => options.negativeText }
					),
					h(
						JMButton,
						{
							size: "large",
							focusable: false,
							onClick: (e: MouseEvent) => {
								e.preventDefault()
								dialogRef?.onPositiveClick?.(e)
								dialogRef?.destroy()
							}
						},
						{ default: () => options.positiveText }
					)
				]
			),
		onPositiveClick: () => {
			options.success()
		},
		onNegativeClick: () => {
			options.cancel?.()
		}
	})
}

export function irreversibleActionDialog(dialog: DialogApiInjection, t: ComposerTranslation, cb: () => void) {
	confirmDialog(dialog, {
		title: t<I18nString>(`common.misc.warning`),
		content: t<I18nString>(`form.messages.irreversible_action_confirmation`),
		positiveText: t<I18nString>(`common.misc.proceed`),
		negativeText: t<I18nString>(`common.misc.cancel`),
		success: () => {
			cb()
		}
	})
}

export function infoDialog(
	dialog: DialogApiInjection,
	options: {
		icon?: () => VNodeChild
		action?: () => VNodeChild
		title: string
		content?: string
		positiveText?: string
		negativeText?: string
		success?: () => void
		cancel?: () => void
		inverted?: boolean
	}
) {
	const dialogRef = dialog.warning({
		class: `p-0! w-[90vw]! max-w-102! ${options.inverted ? "bg-primary! [&_.n-base-close]:text-white!" : ""}`,
		titleClass: options.icon ? "p-6! pb-4!" : undefined,
		icon: options.icon,
		content: () =>
			h(
				"div",
				{
					class: "flex flex-col gap-4"
				},
				[
					options.title &&
						h(
							"div",
							{
								class: `text-fs-xxl font-bold leading-tight ${options.inverted ? "text-white" : ""}`
							},
							options.title
						),
					options.content &&
						h(
							"div",
							{
								class: `text-fs-base leading-tight ${options.inverted ? "text-primary-300" : "text-secondary"}`
							},
							options.content
						),
					(options.positiveText || options.negativeText) &&
						h(
							"div",
							{
								class: "flex flex-wrap items-center justify-start gap-4 mt-2"
							},
							[
								options.positiveText &&
									h(
										JMButton,
										{
											size: "large",
											type: options.inverted ? "secondary" : "default",
											focusable: false,
											onClick: (e: MouseEvent) => {
												e.preventDefault()
												dialogRef?.onPositiveClick?.(e)
												dialogRef?.destroy()
											}
										},
										{ default: () => options.positiveText }
									),
								options.negativeText &&
									h(
										JMButton,
										{
											type: options.inverted ? "default" : "secondary",
											size: "large",
											focusable: false,
											onClick: (e: MouseEvent) => {
												e.preventDefault()
												dialogRef?.onNegativeClick?.(e)
												dialogRef?.destroy()
											}
										},
										{ default: () => options.negativeText }
									)
							]
						)
				]
			),
		contentClass: "p-6! pt-4! my-0!",
		actionClass: "p-6! pt-0!",
		closeFocusable: false,
		showIcon: !!options.icon,
		bordered: false,
		action: options.action,
		onPositiveClick: () => {
			options.success?.()
		},
		onNegativeClick: () => {
			options.cancel?.()
		}
	})
}

export function getNavigatorLanguage(): string {
	// TODO-NEXT: MVP2 replace "it" with ""
	return (_split(navigator.language, "-")[0] || "it").toLowerCase()
}

export function handleDelete(cb: () => void, options: { dialog: DialogApiInjection; t: ComposerTranslation }) {
	irreversibleActionDialog(options.dialog, options.t, () => {
		cb()
	})
}

export function handleRouteLeave(options: { dialog: DialogApiInjection; t: ComposerTranslation }): Promise<boolean> {
	return new Promise<boolean>(resolve => {
		confirmDialog(options.dialog, {
			title: options.t<I18nString>(`common.misc.warning`),
			content: options.t<I18nString>(`form.messages.unsaved_changes_warning`),
			positiveText: options.t<I18nString>(`common.misc.proceed`),
			negativeText: options.t<I18nString>(`common.misc.cancel`),
			success: () => {
				resolve(true)
			},
			cancel: () => {
				resolve(false)
			}
		})
	})
}
